import React, { createContext, useContext, useState } from 'react';
import Toast from '../../components/Toast';
import { AlertColor } from '@mui/material';
import { extractError } from '@/lib/helpers/helpers';
import { useDispatch } from 'react-redux';
import { editNote } from '@/state/calendar/calendarSlice';

export type IconStatus = 'error' | 'success' | 'loading' | 'none' | undefined;

export interface ToastOptions {
    label?: string;
    severity?: AlertColor;
    icon?: IconStatus;
    duration?: number;
}

export interface TogglePromise {
    promiseFunc: (payload?: any) => any;
    successLabel?: string;
    errorLabel?: string;
    duration?: number;
    payload: any;
}

export interface ToastContextInterface {
    toggleToast: (isOpen: boolean, options: ToastOptions) => void;
    toggleToastPromise: (options: TogglePromise) => Promise<any>;
}

export const ToastContext = createContext<ToastContextInterface>({
    toggleToast: (isBoolean, options) => {},
    toggleToastPromise: (options) => {
        return new Promise((resolve) => {});
    },
});
export const useToastCtx = () => useContext<ToastContextInterface>(ToastContext);

export const ToastProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [toastOptions, setToastOptions] = useState<ToastOptions>({
        label: 'Loading...',
        severity: 'info',
        icon: 'loading',
    });

    const onClose = () => {
        setIsOpen(false);
    };

    const toggleToast = (isOpen: boolean, options: ToastOptions) => {
        setIsOpen(isOpen);
        setToastOptions(options);

        setTimeout(() => {
            setIsOpen(false);
        }, options?.duration || 1200);
    };

    const toggleToastPromise = async ({
        promiseFunc,
        successLabel = '',
        errorLabel = '',
        duration = 3500,
        payload = {},
    }: TogglePromise): Promise<any> => {
        try {
            setIsOpen(true);
            setToastOptions({ label: 'Sending...', severity: 'info', icon: 'loading' });
            const result = await promiseFunc(payload);

            if (payload.id) {
                dispatch(editNote(payload));
            }

            setToastOptions({
                label: successLabel || 'Done',
                severity: 'success',
                icon: 'success',
            });
            return result;
        } catch (error) {
            console.log(error);
            const errorMassage = extractError(error);
            console.log(errorMassage);
            setToastOptions({
                label: errorLabel || `${errorMassage.error}, ${errorMassage.message}`,
                severity: 'error',
                icon: 'error',
            });
            throw error;
        } finally {
            setTimeout(() => {
                setIsOpen(false);
            }, duration);
        }
    };

    return (
        <ToastContext.Provider value={{ toggleToast, toggleToastPromise }}>
            {children}
            <Toast
                open={isOpen}
                label={toastOptions.label || ''}
                severity={toastOptions.severity || 'info'}
                onClose={onClose}
                icon={toastOptions.icon}
            />
        </ToastContext.Provider>
    );
};
